import { Container, Text, type Texture } from 'pixi.js';
import type { RoadLandmark } from '@goodtime/shared';
import { chapterTitle, landmarkAlias, landmarkName, roadStripBox } from '../../logic/road/landmarks';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import type { TitleCardOptions } from '../../widgets/board/TitleCard';
import { ChapterDialog, type NextSlot } from '../../widgets/road/ChapterDialog';
import { LandmarkRoad } from '../../widgets/road/LandmarkRoad';
import { RestoreDialog, type RestoreOutcome } from '../../widgets/road/RestoreDialog';
import { RestoreMoment } from '../../widgets/road/RestoreMoment';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

export type { RestoreOutcome } from '../../widgets/road/RestoreDialog';

/** What the host gives the road: its landmarks and how to restore one. */
export interface LandmarkRoadView {
  /** The slot (for the chapter's name). */
  themeId: string;
  /** The road's chapter in the story. */
  chapter: number;
  landmarks: readonly RoadLandmark[];
  /** The slot finishing the chapter opens, and how to go and play it. */
  next?: NextSlot | null;
  /** Ask the server to restore landmark `index`. */
  restore: (index: number) => Promise<RestoreOutcome>;
  /** A restore card opened (for the funnel). */
  onOpened?: (index: number) => void;
  /** Screen kept clear at the bottom (the home indicator), in px. */
  safeBottom?: number;
}

/**
 * Royal Road's landmarks on the slot canvas (Phase 2a): the road under the
 * SPIN row, a ruin's restore card, and the restore moment. The road sits
 * under the HUD; the card and the moment go in {@link overlay}, which the
 * scene keeps above everything.
 */
export class LandmarkRoadComponent extends Container implements SceneComponent {
  /** The card and the moment: the scene puts this on top of the stage. */
  readonly overlay = new Container();
  private readonly road: LandmarkRoad;
  private readonly dialog: RestoreDialog;
  private moment: RestoreMoment | null = null;
  private readonly chapterDialog: ChapterDialog;
  /** The chapter-done moment is playing (the title card). */
  private celebrating = false;
  private view: LandmarkRoadView | null = null;
  private ctx: SceneLayoutContext | null = null;

  constructor(
    private readonly deps: {
      texture: (alias: string) => Texture | null;
      /** The slot's reel floor, for the card's board. */
      floor: () => Texture | null;
      /** The player's coins. */
      coins: () => number;
      /** Where the top bar's coin counter is. */
      wallet: () => { x: number; y: number } | null;
      /** Whether a card may open now (the reels are still, no run is on). */
      canOpen: () => boolean;
      /** The TV title card, over everything. */
      titleCard: (text: string, options: TitleCardOptions) => Promise<void>;
    },
  ) {
    super();
    this.road = new LandmarkRoad(deps.texture);
    this.road.onTap = (i) => this.openCard(i);
    this.dialog = new RestoreDialog(deps.texture, deps.floor);
    this.chapterDialog = new ChapterDialog(deps.texture, deps.floor);
    this.addChild(this.road);
    this.overlay.addChild(this.dialog, this.chapterDialog);
    this.visible = false;
  }

  /** True while a card or a restore moment is on screen: SPIN waits. */
  get blocking(): boolean {
    return this.dialog.isOpen || this.chapterDialog.isOpen || this.moment !== null || this.celebrating;
  }

  /** The road to show, or null to hide it. */
  setView(view: LandmarkRoadView | null): void {
    this.view = view;
    this.visible = view !== null;
    if (!view) {
      this.dialog.close();
      this.chapterDialog.close();
    }
    this.road.setLandmarks(view?.landmarks ?? []);
    if (this.ctx) this.layout(this.ctx);
  }

  /** The road's art has loaded: draw the landmarks again with it (a road
   *  drawn sooner falls back to whatever painting had arrived). */
  refreshArt(): void {
    if (this.view && this.ctx) this.layout(this.ctx);
  }

  /** No taps while a Wheel Run is on. */
  setEnabled(enabled: boolean): void {
    this.road.setEnabled(enabled);
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    const box =
      ctx.machineLeft === undefined || ctx.machineRight === undefined
        ? null
        : roadStripBox(
            { left: ctx.machineLeft, right: ctx.machineRight, spinRowY: ctx.spinButtonY },
            { w: ctx.width, h: ctx.height },
            this.view?.safeBottom ?? 0,
          );
    this.road.layout(this.view ? box : null);
    this.dialog.layout(ctx.width, ctx.height);
    this.chapterDialog.layout(ctx.width, ctx.height);
  }

  private openCard(index: number): void {
    const view = this.view;
    if (!view || this.blocking || !this.deps.canOpen()) return;
    // The whole road restored: its landmarks bring back the chapter's card,
    // which no longer says the next slot has just opened.
    if (view.landmarks.every((l) => l.restored)) {
      this.chapterDialog.open(view.themeId, view.chapter, view.next ?? null, 'again');
      return;
    }
    view.onOpened?.(index);
    this.dialog.open({
      landmarks: view.landmarks,
      index,
      coins: this.deps.coins(),
      next: view.next?.name ?? null,
      nextOwned: view.next?.owned ?? false,
      restore: () => view.restore(index),
      onRestored: (outcome) => {
        if (outcome.charged > 0) void this.play(index, outcome.chapterComplete ? outcome : null);
      },
    });
  }

  /** The restore moment for landmark `index`, then it's back on the road,
   *  or on to the chapter's end when this restore finished it (`chapter`). */
  private async play(index: number, chapter: Extract<RestoreOutcome, { kind: 'restored' }> | null): Promise<void> {
    const ctx = this.ctx;
    const l = this.view?.landmarks[index];
    // From the ruin, home to its restored painting (sized to match the others').
    const from = this.road.spot(index, false);
    if (!ctx || !l || !from) return;
    this.road.setAway(index);
    const moment = new RestoreMoment({
      name: landmarkName(l.id),
      ruined: this.deps.texture(landmarkAlias(l.id, false)),
      restored: this.deps.texture(landmarkAlias(l.id, true)),
      coin: this.deps.texture('shared:icon:coin'),
      from,
      to: this.road.spot(index, true),
      wallet: this.deps.wallet(),
      width: ctx.width,
      height: ctx.height,
    });
    this.moment = moment;
    this.overlay.addChild(moment);
    await moment.done;
    this.overlay.removeChild(moment);
    moment.destroy({ children: true });
    this.moment = null;
    this.road.setAway(null);
    if (chapter && this.view) await this.chapterDone(this.view, chapter.opened);
  }

  /** The chapter-done moment: the TV title card with its fanfare, then the
   *  card for the slot it opened (`opened`), or that the player already had. */
  private async chapterDone(view: LandmarkRoadView, opened: boolean): Promise<void> {
    this.celebrating = true;
    await this.deps.titleCard('COMPLETE!', {
      sub: chapterTitle(view.themeId, view.chapter),
      sting: ROAD_SOUNDS.chapterDone,
      kind: 'complete',
    });
    this.celebrating = false;
    this.chapterDialog.open(view.themeId, view.chapter, view.next ?? null, opened ? 'opened' : 'owned');
  }

  /**
   * What's on screen, for browser tests: each landmark's square and state,
   * and every piece of text showing (the price tag, the card, the moment's
   * line) with its centre, to read and to tap.
   */
  debug(): {
    landmarks: { id: string; restored: boolean; x: number; y: number; size: number }[];
    texts: { text: string; x: number; y: number }[];
    card: boolean;
    moment: boolean;
    titleCard: boolean;
  } {
    const landmarks = (this.view?.landmarks ?? []).map((l, i) => ({
      id: l.id,
      restored: l.restored,
      ...(this.road.spot(i) ?? { x: 0, y: 0, size: 0 }),
    }));
    const texts: { text: string; x: number; y: number }[] = [];
    const walk = (c: Container) => {
      if (!c.visible) return;
      if (c instanceof Text) {
        const b = c.getBounds();
        texts.push({
          text: c.text,
          x: Math.round(b.x + b.width / 2),
          y: Math.round(b.y + b.height / 2),
        });
      }
      for (const child of c.children) walk(child as Container);
    };
    if (this.visible) walk(this);
    walk(this.overlay);
    return {
      landmarks,
      texts,
      card: this.dialog.isOpen || this.chapterDialog.isOpen,
      moment: this.moment !== null || this.celebrating,
      titleCard: this.celebrating,
    };
  }

  tick(deltaMs: number): void {
    if (!this.visible) return;
    this.road.setCoins(this.deps.coins());
    this.road.tick(deltaMs);
    this.moment?.tick(deltaMs);
  }
}
